import React from "react";
import TuneIcon from "@mui/icons-material/Tune";
import EmployeeSchedulingPreferences from "./EmployeeSchedulingPreferences";
import { getInitials } from "../../utilities/helpers";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import FiberPinIcon from "@mui/icons-material/FiberPin";
import EditIcon from "@mui/icons-material/Edit";
import Tooltip from "@mui/material/Tooltip";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import LoadSpinner from "../../../../../utilities/LoadSpinner";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import StoreName from "../../../home/top_bar_components/StoreName";
import EmployeeProfileAvailabilityTab from "./EmployeeProfileAvailabilityTab";
import EmployeeProfileCertificationsTab from "./EmployeeProfileCertificationsTab";
import EmployeeProfileTrainingTab from "./EmployeeProfileTrainingTab";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import EmployeeProfileAttributesTab from "./EmployeeProfileAttributesTab";
import EmployeeProfileStatisticsTab from "./EmployeeProfileStatisticsTab";
import EmployeeProfileNotesTab from "./EmployeeProfileNotesTab";
import EmployeeProfileCheckInsTab from "./EmployeeProfileCheckInsTab";
import EmployeeManageAccessTab from "./EmployeeManageAccessTab";
import EmployeeProfileTimeOffTab from "./EmployeeProfileTimeOffTab";
import EmployeeProfilePayTab from "./EmployeeProfilePayTab";
import WarningIcon from "@mui/icons-material/Warning";
import EmployeeNotLoggedInWarningModal from "./EmployeeNotLoggedInWarningModal";
import EmployeeDeleteWarningModal from "./EmployeeDeleteWarningModal";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonIcon from "@mui/icons-material/Person";
import DescriptionIcon from "@mui/icons-material/Description";
import NotesIcon from "@mui/icons-material/Notes";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import SecurityIcon from "@mui/icons-material/Security";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import { formatPhoneNumber } from "../../../../../helpers/helpers";
import {
  SPECIAL_ACCESS_LABELS,
  SPECIAL_ACCESS_ICONS,
  SPECIAL_ACCESS_STYLES,
} from "./EmployeesTableRow";

function EmployeeProfile(props) {
  const [activeTab, setActiveTab] = React.useState("Availability");

  const canManageSchedulingPreferences = props.schedulingEnabled !== false &&
    ["company_manager", "company_admin", "rightwork_internal"].includes(props.permissionRole);
  // Pay (classification, wages) is admin-only; managers still see payroll ID.
  const canManagePay = ["company_admin", "rightwork_internal"].includes(props.permissionRole);

  const storeTimezone = React.useMemo(() => {
    return props.storeNames?.find((store) => store.id === props.homeStore)?.timezone || null;
  }, [props.storeNames, props.homeStore]);

  const isAutoGeneratedEmail =
    props.selectedTeamMemberData?.email?.endsWith("@getrightwork.com") &&
    props.selectedTeamMemberData?.email?.length > 30;

  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  const [openNotLoggedInModal, setOpenNotLoggedInModal] = React.useState(false);
  const handleOpenNotLoggedInModal = () => {
    setOpenNotLoggedInModal(true);
  };
  const handleCloseNotLoggedInModal = () => {
    setOpenNotLoggedInModal(false);
  };

  const [openEmployeeDeleteWarningModal, setOpenEmployeeDeleteWarningModal] = React.useState(false);
  const handleOpenEmployeeDeleteWarningModal = () => {
    setOpenEmployeeDeleteWarningModal(true);
  };
  const handleCloseEmployeeDeleteWarningModal = () => {
    setOpenEmployeeDeleteWarningModal(false);
  };

  const [selectedStmId, setSelectedStmId] = React.useState(null);
  const [selectedStoreId, setSelectedStoreId] = React.useState(null);
  const [selectedStoreName, setSelectedStoreName] = React.useState(null);

  const handleEditClick = () => {
    props.setModalEdit(true);
    props.handleOpen();
  };

  const handleDeleteButtonClick = () => {
    // Find the team member record for the current store
    const currentStoreTeamMember = props.relatedTeamMembersData?.find(
      (member) => member.store?.id === props.homeStore
    );

    if (currentStoreTeamMember) {
      // Set the selected store information
      setSelectedStmId(currentStoreTeamMember.id);
      setSelectedStoreId(currentStoreTeamMember.store?.id);
      setSelectedStoreName(currentStoreTeamMember.store?.store_name);

      // Switch to Manage Access tab
      setActiveTab("Manage Access");

      // Open the delete modal
      handleOpenEmployeeDeleteWarningModal();
    }
  };

  const tabs = React.useMemo(() => {
    const notesTab = props.checkInsEnabled
      ? { id: "Notes", label: "Check-Ins", icon: AssignmentIndIcon }
      : { id: "Notes", label: "Notes", icon: NotesIcon };
    // Availability and Time Off are scheduling/approvals workflows; hide them
    // when the store's scheduling master flag is off.
    const schedulingTabs = props.schedulingEnabled === false
      ? []
      : [
          { id: "Availability", label: "Availability", icon: AccessTimeIcon },
          { id: "Time Off", label: "Time Off", icon: EventBusyIcon },
        ];
    const baseTabs = [
      ...schedulingTabs,
      { id: "Attributes", label: "Roles & Skills", icon: PersonIcon },
      ...(canManageSchedulingPreferences ? [{ id: "Scheduling Preferences", label: "Scheduling Preferences", icon: TuneIcon }] : []),
      { id: "Certifications", label: "Documents", icon: DescriptionIcon },
      { id: "Training", label: "Training", icon: SchoolOutlinedIcon },
      // { id: "Statistics", label: "Statistics", icon: BarChartIcon },
      notesTab,
      { id: "Pay & Timekeeping", label: "Pay & Timekeeping", icon: MonetizationOnIcon },
      { id: "Manage Access", label: "Manage Access", icon: SecurityIcon },
    ];
    return baseTabs;
  }, [props.checkInsEnabled, props.schedulingEnabled, canManageSchedulingPreferences]);

  React.useEffect(() => {
    // Keep activeTab valid as the tab set changes (Availability/Time Off
    // hidden when scheduling is off).
    if (!tabs.some((t) => t.id === activeTab)) {
      setActiveTab(tabs[0]?.id || "Attributes");
    }
  }, [tabs, activeTab]);

  const backControls = (
    <div className="flex items-center gap-4">
      {props.profileBackOverride ? (
        <>
          <button
            className="flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
            onClick={() => {
              props.setShowUserProfile(false);
              props.profileBackOverride.onClick();
            }}
          >
            <ArrowBackIcon style={{ fontSize: "18px" }} />
            <span>{props.profileBackOverride.label}</span>
          </button>
          <span className="w-px h-4 bg-gray-200" />
          <button
            className="flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
            onClick={() => {
              props.profileBackOverride.onLeave?.();
              props.setShowUserProfile(false);
            }}
          >
            <PeopleOutlineIcon style={{ fontSize: "18px" }} />
            <span>See all employees</span>
          </button>
        </>
      ) : (
        <button
          className="flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
          onClick={() => props.setShowUserProfile(false)}
        >
          <ArrowBackIcon style={{ fontSize: "18px" }} />
          <span>Back to employees</span>
        </button>
      )}
    </div>
  );

  if (!props.selectedTeamMemberData) {
    return (
      <div className="bg-white shadow-sm rounded-lg overflow-hidden flex flex-col min-h-screen">
        <div className="flex items-center justify-between gap-3 px-6 py-3 border-b border-gray-200 md:px-4">
          {backControls}
        </div>
        <div className="flex-1 flex items-center justify-center">
          {props.selectedTeamMemberLoadFailed ? (
            <div className="flex flex-col items-center gap-3 text-center">
              <p className="text-sm text-slate-600">
                We couldn't load this employee's profile.
              </p>
              {props.retrySelectedTeamMember && (
                <button
                  type="button"
                  onClick={props.retrySelectedTeamMember}
                  className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:border-violet-700 hover:text-violet-700"
                >
                  Try again
                </button>
              )}
            </div>
          ) : (
            <LoadSpinner />
          )}
        </div>
      </div>
    );
  }

  const showNotLoggedInWarning =
    !props.selectedTeamMemberData.user?.has_logged_in && !isAutoGeneratedEmail;

  return (
    <>
      <div className="bg-white shadow-sm rounded-lg overflow-hidden flex flex-col min-h-screen">
        {/* Back navigation + store context */}
        <div className="flex items-center justify-between gap-3 px-6 py-3 border-b border-gray-200 md:px-4">
          {backControls}
          <StoreName
            storeOptions={props.storeOptions || []}
            homeStore={props.homeStore}
            storeNames={props.storeNames}
            change_home_store={props.change_home_store}
          />
        </div>

        {/* Compact Header */}
        <div className="flex items-center gap-4 px-6 py-4 border-b border-gray-200 md:px-4 md:flex-wrap">
          {/* Avatar */}
          <div
            className={`w-14 h-14 shrink-0 rounded-full flex items-center justify-center ${props.avatarCardColor} text-white text-lg font-semibold shadow ring-2 ring-white`}
          >
            {getInitials(
              props.selectedTeamMemberData.first_name,
              props.selectedTeamMemberData.last_name
            )}
          </div>

          {/* Identity */}
          <div className="flex-1 min-w-[220px]">
            {/* Name + status badges */}
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-bold text-gray-900 truncate">
                {props.selectedTeamMemberData.first_name}{" "}
                {props.selectedTeamMemberData.last_name}
              </h1>

              <span
                className={`${props.permissionInfo.style} px-2.5 py-0.5 rounded-md text-xs font-medium`}
              >
                {props.permissionInfo.label}
              </span>

              {props.selectedTeamMemberData.is_minor && (
                <span className="bg-slate-100 border border-slate-300 text-slate-600 px-2.5 py-0.5 rounded-md text-xs font-medium">
                  Minor
                </span>
              )}

              {props.selectedTeamMemberData?.permission_type_overrides?.map(
                (accessKey) => {
                  const Icon = SPECIAL_ACCESS_ICONS[accessKey];
                  const styles = SPECIAL_ACCESS_STYLES[accessKey];
                  const label = SPECIAL_ACCESS_LABELS[accessKey];
                  if (!Icon || !styles || !label) return null;
                  return (
                    <Tooltip key={accessKey} title={label} placement="top" arrow>
                      <div
                        className={`w-[22px] h-[22px] rounded-md border ${styles.border} ${styles.bg} flex items-center justify-center`}
                      >
                        <Icon className={styles.text} style={{ fontSize: "14px" }} />
                      </div>
                    </Tooltip>
                  );
                }
              )}

              {showNotLoggedInWarning && (
                <Tooltip
                  title={`${props.selectedTeamMemberData.first_name} has not logged in yet. Click here if you would like to ${props.selectedTeamMemberData?.welcome_send_count > 0 ? "send the welcome email again" : "send the welcome email"}.`}
                >
                  <span
                    className="flex items-center cursor-pointer"
                    onClick={handleOpenNotLoggedInModal}
                  >
                    <WarningAmberIcon
                      className="text-orange-600"
                      style={{ fontSize: "18px" }}
                    />
                  </span>
                </Tooltip>
              )}

              {isAutoGeneratedEmail && (
                <Tooltip title="Auto-generated profile - click to add real email.">
                  <span
                    className="flex items-center cursor-pointer"
                    onClick={handleEditClick}
                  >
                    <WarningIcon
                      className="text-rose-600"
                      style={{ fontSize: "18px" }}
                    />
                  </span>
                </Tooltip>
              )}
            </div>

            {/* Contact info */}
            <div className="flex items-center flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-gray-600">
              <span className="flex items-center gap-1.5 min-w-0">
                <EmailIcon
                  className="text-gray-400 shrink-0"
                  style={{ fontSize: "16px" }}
                />
                <span className="truncate">
                  {props.selectedTeamMemberData.email || "None"}
                </span>
              </span>

              <span className="flex items-center gap-1.5">
                <PhoneIcon
                  className="text-gray-400 shrink-0"
                  style={{ fontSize: "16px" }}
                />
                {props.selectedTeamMemberData.phone_number
                  ? formatPhoneNumber(props.selectedTeamMemberData.phone_number)
                  : "None"}
              </span>

              {props.timeEntriesEnabled && (
                <span className="flex items-center gap-1.5">
                  <FiberPinIcon
                    className="text-gray-400 shrink-0"
                    style={{ fontSize: "16px" }}
                  />
                  {props.selectedTeamMemberData.clock_pin || "None"}
                </span>
              )}
            </div>
          </div>

          {/* Header actions */}
          <div className="shrink-0 flex items-center gap-2 md:w-full md:justify-end">
            <Tooltip title="Edit employee info">
              <button
                type="button"
                onClick={handleEditClick}
                className="inline-flex h-9 items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-1"
              >
                <EditIcon style={{ fontSize: "16px" }} />
                <span>Edit</span>
              </button>
            </Tooltip>
            <Tooltip title="Delete from this store">
              <button
                type="button"
                onClick={handleDeleteButtonClick}
                className="inline-flex h-9 items-center gap-1.5 rounded-md border border-rose-200 bg-rose-50 px-3 text-sm font-medium text-rose-700 transition-colors hover:border-rose-300 hover:bg-rose-100 hover:text-rose-800 focus:outline-none focus:ring-2 focus:ring-rose-500 focus:ring-offset-1"
              >
                <DeleteIcon style={{ fontSize: "16px" }} />
                <span>Delete</span>
              </button>
            </Tooltip>
          </div>
        </div>


        {/* Body: nav rail + tab content */}
        <div className="flex min-w-0 flex-1 lg:flex-col">
          {/* Navigation Rail */}
          <nav className="shrink-0 w-64 min-w-0 p-3 flex flex-col gap-1 border-r border-gray-200 bg-white lg:w-full lg:flex-row lg:overflow-x-auto lg:border-r-0 lg:border-b lg:gap-2">
            {tabs.map((tab) => {
              const IconComponent = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                    isActive
                      ? "bg-violet-50 text-violet-700"
                      : "text-gray-500 hover:bg-gray-50 hover:text-gray-700"
                  }`}
                  onClick={() => handleTabClick(tab.id)}
                >
                  <IconComponent style={{ fontSize: "18px" }} />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>

          {/* Tab Content */}
          <div className="flex-1 min-w-0 bg-white min-h-[600px] p-8 lg:p-6 md:p-4">
            {props.schedulingEnabled !== false && activeTab === "Availability" && (
              <EmployeeProfileAvailabilityTab
                selectedTeamMemberData={props.selectedTeamMemberData}
                selectedTeamMemberAvailabilityData={
                  props.selectedTeamMemberAvailabilityData
                }
                create_availability_request={props.create_availability_request}
                manager_rescind_availability_request={props.manager_rescind_availability_request}
              />
            )}

            {canManageSchedulingPreferences && activeTab === "Scheduling Preferences" && (
              <EmployeeSchedulingPreferences inline open member={props.selectedTeamMemberData}
                onSave={props.updatePersonInfo} />
            )}

            {activeTab === "Attributes" && (
              <EmployeeProfileAttributesTab
                selectedTeamMemberData={props.selectedTeamMemberData}
                roleNames={props.roleNames}
                homeStore={props.homeStore}
                updatePersonInfo={props.updatePersonInfo}
                proficiencyStarsCount={props.proficiencyStarsCount}
                proficiencyFromLMSEnabled={props.proficiencyFromLMSEnabled}
              />
            )}

            {activeTab === "Certifications" && (
              <EmployeeProfileCertificationsTab
                certificateData={props.certificateData}
                post_certificate={props.post_certificate}
                delete_certificate={props.delete_certificate}
                stm_id={props.selectedTeamMemberData?.id}
                homeCompany={props.homeCompany}
                homeStore={props.homeStore}
                onRefresh={props.fetchStoreTeamMember}
              />
            )}

            {activeTab === "Training" && (
              <EmployeeProfileTrainingTab
                stm_id={props.selectedTeamMemberData?.id}
                homeCompany={props.homeCompany}
                homeStore={props.homeStore}
                certificateData={props.certificateData}
                onRefresh={props.fetchStoreTeamMember}
                isSelfView={false}
              />
            )}

            {activeTab === "Statistics" && (
              <EmployeeProfileStatisticsTab
                selectedTeamMemberData={props.selectedTeamMemberData}
              />
            )}

            {activeTab === "Notes" && (
              props.checkInsEnabled ? (
                <EmployeeProfileCheckInsTab
                  selectedTeamMemberData={props.selectedTeamMemberData}
                  stm_id={props.selectedTeamMemberData?.id}
                  get_file_upload={props.get_file_upload}
                  homeCompany={props.homeCompany}
                  homeStore={props.homeStore}
                />
              ) : (
                <EmployeeProfileNotesTab
                  selectedTeamMemberData={props.selectedTeamMemberData}
                  post_logbook_employee_entry={props.post_logbook_employee_entry}
                  delete_logbook_employee_entry={props.delete_logbook_employee_entry}
                  stm_id={props.selectedTeamMemberData?.id}
                  get_file_upload={props.get_file_upload}
                  homeCompany={props.homeCompany}
                />
              )
            )}

            {props.schedulingEnabled !== false && activeTab === "Time Off" && (
              <EmployeeProfileTimeOffTab
                selectedTeamMemberData={props.selectedTeamMemberData}
                post_store_team_member={props.post_store_team_member}
                selectedTeamMemberTimeOffData={props.selectedTeamMemberTimeOffData}
                request_time_off_for_employee={props.request_time_off_for_employee}
                rescind_time_off_request={props.rescind_time_off_request}
                storeTimezone={storeTimezone}
              />
            )}

            {activeTab === "Pay & Timekeeping" && (
              <EmployeeProfilePayTab
                selectedTeamMemberData={props.selectedTeamMemberData}
                homeStore={props.homeStore}
                fetchStoreTeamMember={props.fetchStoreTeamMember}
                relatedTeamMembersData={props.relatedTeamMembersData}
                canManagePay={canManagePay}
              />
            )}

            {activeTab === "Manage Access" && (
              <EmployeeManageAccessTab
                selectedTeamMemberData={props.selectedTeamMemberData}
                stm_id={props.selectedTeamMemberData?.id}
                relatedTeamMembersData={props.relatedTeamMembersData}
                homeStore={props.homeStore}
                storeNames={props.storeNames}
                archive_store_team_member={props.archive_store_team_member}
                setShowUserProfile={props.setShowUserProfile}
                storeList={props.storeList}
                clone_or_reactivate_store_team_member={
                  props.clone_or_reactivate_store_team_member
                }
                post_store_team_member_only_permission_type_and_store_id={
                  props.post_store_team_member_only_permission_type_and_store_id
                }
                bulk_update_store_team_member_permissions={
                  props.bulk_update_store_team_member_permissions
                }
                logbookEnabled={props.logbookEnabled}
                maintenanceEnabled={props.maintenanceEnabled}
                itTicketsEnabled={props.itTicketsEnabled}
                marketingTicketsEnabled={props.marketingTicketsEnabled}
                menuTicketsEnabled={props.menuTicketsEnabled}
                permissionRole={props.permissionRole}
                isCompanyAdmin={props.isCompanyAdmin}
              />
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      <EmployeeNotLoggedInWarningModal
        open={openNotLoggedInModal}
        handleClose={handleCloseNotLoggedInModal}
        firstName={props.selectedTeamMemberData.first_name}
        lastName={props.selectedTeamMemberData.last_name}
        homeStore={props.homeStore}
        stm_id={props.selectedTeamMemberData?.id}
        welcomeSendCount={props.selectedTeamMemberData?.welcome_send_count}
        phoneNumber={props.selectedTeamMemberData?.phone_number}
        emailAddress={props.selectedTeamMemberData?.email}
        onWelcomeEmailSent={() => {
          if (typeof props.fetchStoreTeamMember === "function") {
            props.fetchStoreTeamMember(false, props.selectedTeamMemberData?.id);
          }
        }}
      />
      <EmployeeDeleteWarningModal
        open={openEmployeeDeleteWarningModal}
        handleClose={handleCloseEmployeeDeleteWarningModal}
        firstName={props.selectedTeamMemberData.first_name}
        lastName={props.selectedTeamMemberData.last_name}
        homeStore={props.homeStore}
        selectedStoreId={selectedStoreId}
        selectedStoreName={selectedStoreName}
        storeNames={props.storeNames}
        stm_id={props.selectedTeamMemberData?.id}
        selectedStmId={selectedStmId}
        archive_store_team_member={props.archive_store_team_member}
        setShowUserProfile={props.setShowUserProfile}
      />
    </>
  );
}

export default EmployeeProfile;
